import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useSheetLifecycle } from '../hooks/useSheetLifecycle';
import { triggerSelectionHaptic } from '../utils/webHaptics';

interface WebReceiptViewerModalProps {
    visible: boolean;
    imageUrl?: string | null;
    title?: string;
    onClose: () => void;
}

export const WebReceiptViewerModal: React.FC<WebReceiptViewerModalProps> = ({
    visible,
    imageUrl,
    title = 'Receipt',
    onClose,
}) => {
    const { isRendered, isClosing, handleClose } = useSheetLifecycle(visible && Boolean(imageUrl), onClose, 220);

    // Close on Escape key
    useEffect(() => {
        if (!isRendered || isClosing) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                triggerSelectionHaptic();
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isRendered, isClosing, handleClose]);

    if (!isRendered || !imageUrl) return null;

    return (
        <div
            className={`fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 ${
                isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
            }`}
            onClick={() => {
                triggerSelectionHaptic();
                handleClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div
                className={`relative max-w-lg w-full max-h-[90vh] overflow-hidden rounded-2xl flex flex-col items-center justify-center select-none ${
                    isClosing ? 'animate-modal-slide-exit' : 'animate-modal-slide-enter'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Floating Close Button */}
                <button
                    type="button"
                    onClick={() => {
                        triggerSelectionHaptic();
                        handleClose();
                    }}
                    className="absolute top-4 right-4 text-white bg-black/60 hover:bg-black/80 active:scale-95 p-2.5 rounded-full backdrop-blur-md transition-all cursor-pointer z-20 border border-white/10 shadow-lg"
                    aria-label="Close receipt"
                >
                    <X className="w-5 h-5 stroke-[2.5]" />
                </button>

                {/* Receipt Image Container */}
                <div className="relative w-full max-h-[85vh] overflow-auto flex items-center justify-center rounded-xl">
                    <img
                        src={imageUrl}
                        alt={title}
                        className="w-full h-auto object-contain max-h-[85vh] mx-auto rounded-xl shadow-2xl"
                        draggable={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default WebReceiptViewerModal;
